import { IconButton, List, TooltipHost } from "@fluentui/react";
import { mergeStyleSets } from "@uifabric/merge-styles";
import AppSettings from "AppSettings";
import CheckboxField from "components/controls/CheckboxField";
import ComboBoxField from "components/controls/ComboBoxField";
import TextInputField from "components/controls/TextInputField";
import { isExternalOrReadonlyAdmin } from "form/util";
import { constants } from "library/constants";
import { MoveFleetToAnotherClientDialog } from "pages/fleets/MoveFleetToAnotherClientDialog";
import React, { ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setFleetWithPremiums } from "store/fleetForm";
import { useSelector } from "store/hooks";
import { selectClientByInternalNumber } from "store/slices/fleetFormikFormSlice";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { PremiumType, UserRole } from "types/types";
import { formatNumber } from "utils/number";
import { useCurrentRoute } from "../../../../../config/routes";
import Dropzone from "../../../Dropzone/Dropzone";
import { fieldType } from "../../fleetConfig";
import ClientSection from "./ClientSection";

const classes = mergeStyleSets({
  root: {
    display: "flex",
    flexWrap: "wrap",
    padding: "10px",
  },
  item: {
    padding: "20px",
    width: "49%",
    "@media(min-width: 1500px)": {
      width: "33%",
    },
  },
  itemmainins: {
    padding: "20px",
    width: "49%",
    "@media(min-width: 1500px)": {
      width: "49%",
    },
  },
  checkboxAndField: {
    display: "flex",
    alignItems: "flex-end",
    gap: "20px",
  },
  fieldNextToCheckbox: {
    flex: 1,
  },
  uploadFile: {
    width: "100%",
  },
  insurerlist: {
    overflow: "auto",
    maxHeight: 65,
    border: "1px solid #CCC",
    marginTop: 20,
    width: "49%",
    "@media(min-width: 1500px)": {
      width: "49%",
    },
  },
  insurerlistentry: {
    position: "relative",
    boxSizing: "border-box",
    display: "block",
    borderLeft: "3px solid ",
    paddingLeft: 3,
    whiteSpace: "nowrap",
  },
});

export const FleetTab = ({
  userRole,
  formikProps,
  fleetConf,
  fleetRef,
  file,
  setFile,
}) => {
  const theme = useTheme() as IStyledTheme;
  const route = useCurrentRoute();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [moveFleetDialogOpen, setMoveFleetDialogOpen] = useState(false);
  const { values, errors, handleChange, setFieldValue } = formikProps;
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);
  const client = useSelector(selectClientByInternalNumber);

  const insurerList = useSelector((s) => s.fleetForm.insurerList);
  const insurerOptions = useMemo(() => {
    return insurerList?.map((insurer) => ({
      value: insurer.insurerInternalNumber,
      label: insurer.insurerName + " (" + insurer.insurerInternalNumber + ")",
    }));
  }, [insurerList, t]);

  const fleet = useSelector((s) => s.fleetForm.fleetWithPremiums);

  const clientAppSettings = useMemo(() => {
    if (client) {
      const countryCode = taxonomy.Country.byId[client.countryCodeId]?.code;
      const countryAppSettings =
        AppSettings.getInstance().getAppSettingsTypes()[countryCode];
      return countryAppSettings;
    } else {
      return null;
    }
  }, [client, taxonomy]);

  const mtplInsurensInfo = fleet?.mtplInsurersInfo;
  const hullInsurensInfo = fleet?.hullInsurersInfo;

  const fleetLegalInsurersInfo = fleet?.fleetLegalInsurersInfo;

  const mtplInsurers = insurerOptions.filter((item) =>
    mtplInsurensInfo?.find(
      (infoItem) => infoItem.insurerInternalNumber === item.value,
    ),
  );

  const hullInsurers = insurerOptions.filter((item) =>
    hullInsurensInfo?.find(
      (infoItem) => infoItem.insurerInternalNumber === item.value,
    ),
  );

  const fleetLegalInsurers = insurerOptions.filter((item) =>
    fleetLegalInsurersInfo?.find(
      (infoItem) => infoItem.insurerInternalNumber === item.value,
    ),
  );
  const hullOnLandInsurersInfo = fleet?.hullOnLandInsurersInfo;

  const hullOnLandInsurers = insurerOptions.filter((item) =>
    hullOnLandInsurersInfo?.find(
      (infoItem) => infoItem.insurerInternalNumber === item.value,
    ),
  );

  const machineryBreakdownInsurersInfo = fleet?.machineryBreakdownInsurersInfo;
  const machineryBreakdownInsurers = insurerOptions.filter((item) =>
    machineryBreakdownInsurersInfo?.find(
      (infoItem) => infoItem.insurerInternalNumber === item.value,
    ),
  );

  const onRenderCell = (item: any, index: number): JSX.Element => {
    return <div className={classes.insurerlistentry}>{item.label}</div>;
  };

  const isCreateFleet = route === constants.createFleet;
  const isEditFleet = route === constants.editFleet;

  const appSettings = useMemo(() => {
    if (isCreateFleet) {
      return clientAppSettings;
    } else if (isEditFleet) {
      return fleetAppSettings;
    }
  }, [isCreateFleet, isEditFleet, clientAppSettings, fleetAppSettings]);

  let filteredFleetConfForPreview = fleetConf.filter(
    (item) => item.name !== "fleetName" && !item?.hideInPreview,
  );

  if (appSettings) {
    if (!appSettings?.VEHICLE_PREMIUM_TYPES.includes(PremiumType.HORSE_POWER)) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "horsePowerBasedTaxAnnualy" &&
          item.name !== "horsePowerBasedTaxProRata",
      );
    }

    if (!appSettings?.VEHICLE_PREMIUM_TYPES.includes(PremiumType.ACCIDENT)) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "accidentAnnualGrossPremium" &&
          item.name !== "accidentProRataGrossPremium",
      );
    }
    if (!appSettings?.VEHICLE_PREMIUM_TYPES.includes(PremiumType.ASSISTANCE)) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "assistanceAnnualGrossPremium" &&
          item.name !== "assistanceProRataGrossPremium",
      );
    }

    if (!appSettings?.VEHICLE_PREMIUM_TYPES.includes(PremiumType.LEGAL)) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "legalAnnualGrossPremium" &&
          item.name !== "legalProRataGrossPremium",
      );
    }

    if (
      !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
        PremiumType.FLEETLEGAL_PRORATA,
      )
    ) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "fleetLegalProRataAnnualGrossPremium" &&
          item.name !== "fleetLegalProRataProRataGrossPremium" &&
          item.name !== "fleetLegalClosingDateAnnualGrossPremium",
      );
    }

    if (
      !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
        PremiumType.HULL_ON_LAND_PRORATA,
      )
    ) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "hullOnLandProRataAnnualGrossPremium" &&
          item.name !== "hullOnLandProRataProRataGrossPremium" &&
          item.name !== "hullOnLandClosingDateAnnualGrossPremium",
      );
    }

    if (
      !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
        PremiumType.MACHINERY_BREAKDOWN_PRORATA,
      )
    ) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) =>
          item.name !== "machineryBreakdownProRataAnnualGrossPremium" &&
          item.name !== "machineryBreakdownProRataProRataGrossPremium" &&
          item.name !== "machineryBreakdownClosingDateAnnualGrossPremium",
      );
    }

    if (!appSettings?.SHOW_FLEET_MOTOR_SPECIALIST) {
      filteredFleetConfForPreview = filteredFleetConfForPreview.filter(
        (item) => item.name !== "motorSpecialist",
      );
    }
  }
  return (
    <div className={classes.root} ref={fleetRef} id="fleet-tab">
      {isCreateFleet && (
        <>
          <div className={classes.item}>
            <TextInputField
              value={values?.fleetName}
              errors={errors}
              onChange={handleChange}
              required
              name={"fleetName"}
              disabled={false}
              label={"bfm.fleetForm.fleetName.label"}
            />
          </div>
          <ClientSection />
          {!isExternalOrReadonlyAdmin(userRole) &&
            appSettings?.SHOW_FLEET_EXCEL_UPLOAD && (
              <div className={classes.uploadFile}>
                <Dropzone
                  file={file}
                  setFile={setFile}
                  formikProps={formikProps}
                />
              </div>
            )}
        </>
      )}
      {isEditFleet && (
        <>
          <div className={classes.item}>
            <TextInputField
              value={values?.fleetName}
              errors={errors}
              onChange={handleChange}
              required
              name={"fleetName"}
              disabled={false}
              label={"bfm.fleetForm.fleetName.label"}
            />
          </div>
          {filteredFleetConfForPreview.map((item, index) => {
            let value = item?.value;

            if (item.field === "number" || item.field === "float") {
              value = value && formatNumber(value);
            }
            let extra: ReactElement = null;

            if (
              !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.FLEETLEGAL_PRORATA,
              ) &&
              !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.HULL_ON_LAND_PRORATA,
              ) &&
              !appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.MACHINERY_BREAKDOWN_PRORATA,
              ) &&
              (item.name === "mtplProRataGrossPremium" ||
                item.name === "proRataPremiumSum")
            ) {
              extra = <div className={classes.item} />;
            }

            if (item.name === "businessYearStartMonth") {
              return !isExternalOrReadonlyAdmin(userRole) &&
                appSettings?.SHOW_FLEET_EXCEL_UPLOAD ? (
                <>
                  <div className={classes.uploadFile} key={index}>
                    <Dropzone
                      file={file}
                      isOnlyExistingVehiclesAttributesUpdate={
                        isEditFleet || false
                      }
                      setFile={setFile}
                      formikProps={formikProps}
                    />
                  </div>
                  {extra}
                </>
              ) : (
                <>
                  {" "}
                  <div className={classes.item}></div>
                </>
              );
            }
            if (item.field === fieldType.dayMonth) {
              return (
                <div className={classes.item} key={"itemField_" + index}>
                  <div key={item.key}>
                    <TextInputField
                      isDayMonthField
                      onChange={handleChange}
                      value={values[item.name]}
                      errors={errors}
                      name={item.name}
                      disabled={false}
                      label={item.key}
                    />
                  </div>
                  {extra}
                </div>
              );
            }

            if (item.name === "clientName") {
              return (
                <div className={classes.item} key={item.key}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 4,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TextInputField
                        onChange={() => {}}
                        value={value}
                        errors={[]}
                        name=""
                        disabled={item?.onlyPreview}
                        label={item.key}
                      />
                    </div>
                    {userRole === UserRole.admin && (
                      <TooltipHost
                        content={t("bfm.moveFleetToAnotherClient.label")}
                        styles={{ root: { display: "flex" } }}
                      >
                        <IconButton
                          iconProps={{ iconName: "Switch" }}
                          ariaLabel={t("bfm.moveFleetToAnotherClient.label")}
                          onClick={() => setMoveFleetDialogOpen(true)}
                          styles={{ root: { height: 32, marginBottom: 18 } }}
                        />
                      </TooltipHost>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <>
                <div className={classes.item} key={item.key}>
                  <TextInputField
                    onChange={() => {}}
                    value={value}
                    errors={[]}
                    name=""
                    disabled={item?.onlyPreview}
                    label={item.key}
                  />
                </div>
                {extra}
              </>
            );
          })}
          {appSettings?.SHOW_MAIN_INSURERS && (
            <>
              <div className={classes.itemmainins}>
                <ComboBoxField
                  disabled={false}
                  errors={[]}
                  name="mainMtplInsurerPartnerNumber"
                  label={"bfm.fleetForm.mainMtplInsurerPartnerName.label"}
                  setFieldValue={setFieldValue}
                  options={insurerOptions || []}
                  defaultValue={values?.mainMtplInsurerPartnerNumber}
                  confidentiality={null}
                />
              </div>
              {appSettings?.SHOW_MAIN_INSURERS_LIST ? (
                <div className={classes.insurerlist} data-is-scrollable>
                  <List
                    items={mtplInsurers}
                    onRenderCell={onRenderCell}
                    style={{
                      color: theme.palette.black,
                    }}
                  />
                </div>
              ) : null}
              <div className={classes.itemmainins}>
                <ComboBoxField
                  disabled={false}
                  defaultOpen={true}
                  errors={[]}
                  name="mainHullInsurerPartnerNumber"
                  label={"bfm.fleetForm.mainHullInsurerPartnerName.label"}
                  setFieldValue={setFieldValue}
                  options={insurerOptions || []}
                  defaultValue={values?.mainHullInsurerPartnerNumber}
                  confidentiality={null}
                />
              </div>
              {appSettings?.SHOW_MAIN_INSURERS_LIST ? (
                <div className={classes.insurerlist} data-is-scrollable>
                  <List
                    items={hullInsurers}
                    onRenderCell={onRenderCell}
                    style={{
                      color: theme.palette.black,
                    }}
                  />
                </div>
              ) : null}

              {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.FLEETLEGAL_PRORATA,
              ) ? (
                <>
                  <div className={classes.itemmainins}>
                    <ComboBoxField
                      disabled={false}
                      defaultOpen={true}
                      errors={[]}
                      name="mainFleetLegalInsurerPartnerNumber"
                      label={
                        "bfm.fleetForm.mainFleetLegalInsurerPartnerName.label"
                      }
                      setFieldValue={setFieldValue}
                      options={insurerOptions || []}
                      defaultValue={values?.mainFleetLegalInsurerPartnerNumber}
                      confidentiality={null}
                    />
                  </div>
                  {appSettings?.SHOW_MAIN_INSURERS_LIST ? (
                    <div className={classes.insurerlist} data-is-scrollable>
                      <List
                        items={fleetLegalInsurers}
                        onRenderCell={onRenderCell}
                        style={{
                          color: theme.palette.black,
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.HULL_ON_LAND_PRORATA,
              ) ? (
                <>
                  <div className={classes.itemmainins}>
                    <ComboBoxField
                      disabled={false}
                      defaultOpen={true}
                      errors={[]}
                      name="mainHullOnLandInsurerPartnerNumber"
                      label={
                        "bfm.fleetForm.mainHullOnLandInsurerPartnerName.label"
                      }
                      setFieldValue={setFieldValue}
                      options={insurerOptions || []}
                      defaultValue={values?.mainHullOnLandInsurerPartnerNumber}
                      confidentiality={null}
                    />
                  </div>
                  {appSettings?.SHOW_MAIN_INSURERS_LIST ? (
                    <div className={classes.insurerlist} data-is-scrollable>
                      <List
                        items={hullOnLandInsurers}
                        onRenderCell={onRenderCell}
                        style={{
                          color: theme.palette.black,
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
                PremiumType.MACHINERY_BREAKDOWN_PRORATA,
              ) ? (
                <>
                  <div className={classes.itemmainins}>
                    <ComboBoxField
                      disabled={false}
                      defaultOpen={true}
                      errors={[]}
                      name="mainMachineryBreakdownInsurerPartnerNumber"
                      label={
                        "bfm.fleetForm.mainMachineryBreakdownInsurerPartnerName.label"
                      }
                      setFieldValue={setFieldValue}
                      options={insurerOptions || []}
                      defaultValue={
                        values?.mainMachineryBreakdownInsurerPartnerNumber
                      }
                      confidentiality={null}
                    />
                  </div>
                  {appSettings?.SHOW_MAIN_INSURERS_LIST ? (
                    <div className={classes.insurerlist} data-is-scrollable>
                      <List
                        items={machineryBreakdownInsurers}
                        onRenderCell={onRenderCell}
                        style={{
                          color: theme.palette.black,
                        }}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
          <div className={classes.item}>
            <div className={classes.checkboxAndField}>
              <CheckboxField
                checked={values?.isRealFleet}
                errors={errors}
                setFieldValue={setFieldValue}
                name={"isRealFleet"}
                disabled={false}
                label={"bfm.fleetForm.isRealFleet.label"}
              />
              <div className={classes.fieldNextToCheckbox}>
                <TextInputField
                  value={values?.fleetNumberInsurer}
                  errors={errors}
                  onChange={handleChange}
                  name={"fleetNumberInsurer"}
                  disabled={false}
                  label={"bfm.fleetForm.fleetNumberInsurer.label"}
                />
              </div>
            </div>
          </div>
          <div className={classes.item}>
            <TextInputField
              value={values?.fleetPolicyNumber}
              errors={errors}
              onChange={handleChange}
              name={"fleetPolicyNumber"}
              disabled={false}
              label={"bfm.fleetForm.fleetPolicyNumber.label"}
            />
          </div>
          {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
            PremiumType.FLEETLEGAL_PRORATA,
          ) ? (
            <div className={classes.item}>
              <TextInputField
                value={values?.fleetLegalPolicyNumber}
                errors={errors}
                onChange={handleChange}
                name={"fleetLegalPolicyNumber"}
                disabled={false}
                label={"bfm.fleetForm.fleetLegalPolicyNumber.label"}
              />
            </div>
          ) : null}
          {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
            PremiumType.FLEETLEGAL_PRORATA,
          ) ? (
            <div className={classes.item}>
              <TextInputField
                value={values?.hullOnLandPolicyNumber}
                errors={errors}
                onChange={handleChange}
                name={"hullOnLandPolicyNumber"}
                disabled={false}
                label={"bfm.fleetForm.hullOnLandPolicyNumber.label"}
              />
            </div>
          ) : null}
          {appSettings?.VEHICLE_PREMIUM_TYPES.includes(
            PremiumType.MACHINERY_BREAKDOWN_PRORATA,
          ) ? (
            <div className={classes.item}>
              <TextInputField
                value={values?.machineryBreakdownPolicyNumber}
                errors={errors}
                onChange={handleChange}
                name={"machineryBreakdownPolicyNumber"}
                disabled={false}
                label={"bfm.fleetForm.machineryBreakdownPolicyNumber.label"}
              />
            </div>
          ) : null}
          <div className={classes.item}>
            <TextInputField
              value={values?.fleetComment}
              errors={errors}
              onChange={handleChange}
              name={"fleetComment"}
              multiline
              rows={5}
              disabled={false}
              label={"bfm.fleetForm.fleetComment.label"}
            />
          </div>
        </>
      )}
      <MoveFleetToAnotherClientDialog
        isOpen={moveFleetDialogOpen}
        fleetId={fleet?.fleetId}
        onDismiss={() => setMoveFleetDialogOpen(false)}
        onSuccess={(movedFleet) =>
          movedFleet && dispatch(setFleetWithPremiums(movedFleet))
        }
      />
    </div>
  );
};
